import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface LeaderboardExclusion {
    id: number;
    user_id: number;
    user_email: string;
    period: string;
    reason: string;
    detected_at?: string | null;
}

interface ExclusionsResponse {
    data: LeaderboardExclusion[];
    meta: { count: number; users: number };
}

const PERIOD_LABELS: Record<string, string> = {
    TODAY: 'Jour',
    WEEKLY: 'Semaine',
    MONTHLY: 'Mois',
    LAST_MONTH: 'Mois dernier',
    YTD: 'YTD',
    ALL_TIME: 'Historique',
};

const PeriodBadge: React.FC<{ period: string }> = ({ period }) => (
    <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        backgroundColor: '#ede9fe',
        color: '#5b21b6',
        border: '1px solid #c4b5fd',
    }}>
        {PERIOD_LABELS[period] || period}
    </span>
);

const AdminLeaderboardExclusionsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [exclusions, setExclusions] = useState<LeaderboardExclusion[]>([]);
    const [usersCount, setUsersCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchExclusions = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/leaderboard/exclusions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const payload: ExclusionsResponse = await response.json();
                setExclusions(payload.data || []);
                setUsersCount(payload.meta?.users || 0);
            }
        } catch (error) {
            console.error('Error fetching leaderboard exclusions:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchExclusions();
        }
    }, [token, hasPermission, fetchExclusions]);

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé. Permission admin_platform requise.</p></Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='gavel' title='Exclusions Leaderboard' />
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p>
                        Exclusions détectées au dernier calcul (antidatage, cash négatif, données incohérentes)
                        {exclusions.length > 0 && ` — ${exclusions.length} exclusion(s) sur ${usersCount} utilisateur(s)`}
                    </p>
                    <button className='button button-purple' onClick={fetchExclusions} disabled={loading}>
                        <i className='material-icons' style={{ fontSize: '18px', marginRight: '5px' }}>refresh</i>
                        Actualiser
                    </button>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    {loading ? (
                        <p>Chargement des exclusions...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Utilisateur</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Période</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Raison</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Détecté le</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exclusions.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                ✅ Aucune exclusion au dernier calcul du leaderboard.
                                            </td>
                                        </tr>
                                    ) : exclusions.map((e, index) => (
                                        <tr key={e.id} style={{ backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                                                {e.user_email}
                                                <span style={{ marginLeft: '8px', color: '#94a3b8', fontWeight: 400, fontSize: '12px' }}>#{e.user_id}</span>
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                <PeriodBadge period={e.period} />
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>{e.reason}</td>
                                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                {e.detected_at ? new Date(e.detected_at).toLocaleString('fr-FR') : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
        </SiteLayout>
    );
};

export default AdminLeaderboardExclusionsScreen;
